import { useState, useEffect } from "react";
import api from "../../../api";
import {
  XCircle,
  User,
  Phone,
  Mail,
  CheckCircle,
  MapPin,
  Building2,
  MessageSquare,
  AlertCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import CustomSelect from "../../../components/ui/CustomSelect";
import { Input } from "../../../components/ui/Input";
import { useNotification } from "../../../context/NotificationContext";

export default function LeadForm({ onClose, refreshLeads, lead }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: "Personne Physique",
    lastName: "",
    firstName: "",
    phone: "",
    email: "",
    linkedinUrl: "",
    organizationName: "",
    siret: "",

    address: "",
    postalCode: "",
    city: "",
    country: "",
    source: "",
    channel: "PENDING",
    interestNotes: "",
    status: "NEW",
    position: "",
    sector: "",
    estimatedWealthBracket: "",
  });
  const [errors, setErrors] = useState({});
  const { showToast } = useNotification();
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);

  // Nouveaux états
  const [countryCode, setCountryCode] = useState("FR (+33)");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);



  useEffect(() => {
    if (lead) {
      let initialPhone = lead.phone || "";
      let initialCountry = "FR (+33)";
      
      const prefixes = [
        { code: "FR (+33)", prefix: "+33" },
        { code: "US (+1)", prefix: "+1" },
        { code: "CA (+1)", prefix: "+1" },
        { code: "CH (+41)", prefix: "+41" },
        { code: "BE (+32)", prefix: "+32" },
        { code: "UK (+44)", prefix: "+44" },
        { code: "DE (+49)", prefix: "+49" },
        { code: "ES (+34)", prefix: "+34" },
        { code: "IT (+39)", prefix: "+39" },
      ];

      for (const p of prefixes) {
        if (initialPhone.startsWith(p.prefix)) {
          initialCountry = p.code;
          initialPhone = initialPhone.replace(p.prefix, "").trim();
          break;
        }
      }
      
      setCountryCode(initialCountry);
      setFormData((prev) => ({ ...prev, ...lead, phone: initialPhone }));
    }
  }, [lead]);

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.lastName?.trim()) newErrors.lastName = "Le nom est obligatoire.";
    if (!formData.firstName?.trim()) {
      newErrors.firstName = "Le prénom est obligatoire.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    // Logique de détection automatique du préfixe téléphonique
    if (name === "phone") {
      const prefixes = [
        { code: "FR (+33)", prefix: "+33" },
        { code: "US (+1)", prefix: "+1" },
        { code: "CA (+1)", prefix: "+1" },
        { code: "CH (+41)", prefix: "+41" },
        { code: "BE (+32)", prefix: "+32" },
        { code: "UK (+44)", prefix: "+44" },
        { code: "DE (+49)", prefix: "+49" },
        { code: "ES (+34)", prefix: "+34" },
        { code: "IT (+39)", prefix: "+39" },
      ];

      for (const p of prefixes) {
        if (value.startsWith(p.prefix)) {
          setCountryCode(p.code);
          value = value.replace(p.prefix, "").trim();
          break;
        }
      }

      // Auto-format simple pour numéro (supprime caractères non numériques, ajoute espace tous les 2 chiffres)
      value = value.replace(/[^\d\s]/g, "");
    }

    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    try {
      const response = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
      const data = await response.json();
      setAddressSuggestions(data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error("Erreur API Adresse :", error);
      setAddressSuggestions([]);
    }
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, adresse: value }));
    searchAddress(value);
  };

  const selectAddress = (feature) => {
    const { name, postcode, city } = feature.properties;
    setFormData((prev) => ({
      ...prev,
      address: name,
      postalCode: postcode,
      city: city,
      country: "France"
    }));
    setShowAddressSuggestions(false);
  };

  const handleSubmit = async () => {
    setLoading(true);

    // ✅ Vérification globale des champs obligatoires
    const newErrors = {};
    if (!formData.lastName?.trim()) newErrors.nom = "Le nom est obligatoire.";
    if (formData.type === "Personne Physique" && !formData.firstName?.trim()) newErrors.prenom = "Le prénom est obligatoire.";
    if (!formData.email?.trim()) newErrors.email = "L'email est obligatoire.";
    if (!formData.address?.trim()) newErrors.adresse = "L'adresse est obligatoire.";

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      showToast("Veuillez remplir tous les champs obligatoires.", "error");
      setLoading(false);
      return;
    }

    try {
      const prefixMatch = countryCode.match(/\(([^)]+)\)/);
      const prefix = prefixMatch ? prefixMatch[1] : "";
      
      const payload = {
        ...formData,
        phone: formData.phone ? `${prefix} ${formData.phone.trim()}`.trim() : "",
        ...(formData.type === "Personne Physique" && {
          organizationName: "",
          siret: "",
          sector: ""
        })
      };

      if (lead) {
        await api.put(`/leads/${lead._id || lead.id}`, payload);
        showToast("Lead modifié avec succès !");
      } else {
        await api.post(`/leads`, payload);
        showToast("Nouveau lead ajouté !");
      }

      refreshLeads();
      showToast("Liste des leads mise à jour !");
      onClose();
    } catch (err) {
      console.error("Erreur lors de l'enregistrement :", err);

      if (err.response?.status === 400) {
        showToast("Erreur de validation : certains champs sont invalides.", "error");
      } else if (err.response?.status === 500) {
        showToast("Erreur serveur : veuillez réessayer plus tard.", "error");
      } else {
        showToast("Erreur inconnue : contactez le support.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const isPM = formData.type === "Personne Morale";

  const steps = isPM
    ? [
      { label: "Identité", icon: <User className="w-5 h-5 mr-2" /> },
      { label: "Coordonnées", icon: <Phone className="w-5 h-5 mr-2" /> },
      { label: "Entreprise", icon: <Building2 className="w-5 h-5 mr-2" /> },
      { label: "Adresse", icon: <MapPin className="w-5 h-5 mr-2" /> },
      { label: "Commentaires", icon: <MessageSquare className="w-5 h-5 mr-2" /> },
    ]
    : [
      { label: "Identité", icon: <User className="w-5 h-5 mr-2" /> },
      { label: "Coordonnées", icon: <Phone className="w-5 h-5 mr-2" /> },
      { label: "Adresse", icon: <MapPin className="w-5 h-5 mr-2" /> },
      { label: "Commentaires", icon: <MessageSquare className="w-5 h-5 mr-2" /> },
    ];

  const stepFields = {
    1: (
      <>
        <div className="col-span-full z-[100] relative mb-2">
          <label className="text-sm font-semibold text-subtle mb-1 block">Type *</label>
          <CustomSelect
            value={formData.type}
            onChange={(val) => setFormData({ ...formData, type: val })}
            options={[
              { label: "Personne Physique", value: "Personne Physique" },
              { label: "Personne Morale", value: "Personne Morale" },
            ]}
          />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">{formData.type === "Personne Morale" ? "Nom du contact *" : "Nom *"}</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <User className="h-4 w-4 text-subtle" aria-hidden="true" />
            </div>
            <Input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              hasError={!!errors.lastName}
              className={`pl-9 ${errors.lastName ? 'pr-10' : ''}`}
              placeholder="Ex: Dupont"
            />
            {errors.lastName && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
              </div>
            )}
          </div>
          {errors.lastName && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.lastName}</p>}
        </div>

          <div>
            <label className="text-sm font-semibold text-subtle mb-1 block">Prénom *</label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <User className="h-4 w-4 text-subtle" aria-hidden="true" />
              </div>
              <Input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                hasError={!!errors.firstName}
                className={`pl-9 ${errors.firstName ? 'pr-10' : ''}`}
                placeholder="Ex: Jean"
              />
              {errors.firstName && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
                </div>
              )}
            </div>
            {errors.firstName && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.firstName}</p>}
          </div>

          <div className="col-span-full mt-2">
            <label className="text-sm font-semibold text-subtle mb-1 block">Poste / Fonction</label>
            <div className="relative">
              <Input
                name="position"
                value={formData.position}
                onChange={handleChange}
                placeholder="Ex: Directeur Général"
              />
            </div>
          </div>
          
          <div className="col-span-full mt-2">
            <label className="text-sm font-semibold text-subtle mb-1 block">Patrimoine estimé</label>
            <CustomSelect
              value={formData.estimatedWealthBracket || ""}
              onChange={(val) => handleChange({ target: { name: "estimatedWealthBracket", value: val } })}
              options={[
                { value: "< 500k€", label: "< 500k€" },
                { value: "500k€ - 1M€", label: "500k€ - 1M€" },
                { value: "1M€ - 5M€", label: "1M€ - 5M€" },
                { value: "> 5M€", label: "> 5M€" }
              ]}
            />
          </div>
      </>
    ),
    2: (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Email *</label>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Mail className="h-4 w-4 text-subtle" aria-hidden="true" />
            </div>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              hasError={!!errors.email}
              className={`pl-9 ${errors.email ? 'pr-10' : ''}`}
              placeholder="exemple@email.com"
            />
            {errors.email && (
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
              </div>
            )}
          </div>
          {errors.email && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.email}</p>}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Téléphone</label>
          <div className="relative flex items-center">
            <div className="absolute inset-y-0 left-0 flex items-center">
              <label htmlFor="country" className="sr-only">Country</label>
              <select
                id="country"
                name="country"
                autoComplete="country"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="h-full rounded-md border-0 bg-transparent py-0 pl-3 text-subtle focus:ring-2 focus:ring-inset focus:ring-accent sm:text-sm w-[115px] outline-none"
              >
                <option value="FR (+33)">FR (+33)</option>
                <option value="US (+1)">US (+1)</option>
                <option value="CA (+1)">CA (+1)</option>
                <option value="CH (+41)">CH (+41)</option>
                <option value="BE (+32)">BE (+32)</option>
                <option value="UK (+44)">UK (+44)</option>
                <option value="DE (+49)">DE (+49)</option>
                <option value="ES (+34)">ES (+34)</option>
                <option value="IT (+39)">IT (+39)</option>
              </select>
            </div>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="pl-[125px]"
              placeholder="6 12 34 56 78"
            />
          </div>
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">LinkedIn</label>
          <div className="flex rounded-md shadow-sm">
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-workspace px-3 text-subtle sm:text-sm bg-surface-muted">
              https://
            </span>
            <Input
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              className="rounded-l-none"
              placeholder="linkedin.com/in/dupont"
            />
          </div>
        </div>
      </>
    ),
    3: isPM ? (
      <>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Dénomination sociale *</label>
          <Input name="organizationName" value={formData.organizationName} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">SIRET</label>
          <Input name="siret" value={formData.siret} onChange={handleChange} />
        </div>
        <div className="col-span-full">
          <label className="text-sm font-semibold text-subtle mb-1 block">Secteur d’activité</label>
          <Input name="sector" value={formData.sector} onChange={handleChange} />
        </div>
      </>
    ) : (
      <>
        <div className="col-span-full relative">
          <label className="text-sm font-semibold text-subtle mb-1 block">Adresse *</label>
          <Input
            name="address"
            value={formData.address}
            onChange={handleAddressChange}
            hasError={!!errors.address}
            className={`${errors.address ? 'pr-10' : ''}`}
            placeholder="Saisissez une adresse..."
          />
          {errors.address && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 pt-6">
              <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
            </div>
          )}
          {errors.address && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.address}</p>}
          {showAddressSuggestions && addressSuggestions?.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {addressSuggestions.map((feature, idx) => (
                <li
                  key={idx}
                  onClick={() => selectAddress(feature)}
                  className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-ink-900 hover:bg-accent-50 hover:text-accent-900"
                >
                  <span className="block truncate">{feature.properties.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Code Postal</label>
          <Input name="postalCode" value={formData.postalCode} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Ville</label>
          <Input name="city" value={formData.city} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Pays</label>
          <Input name="country" value={formData.country} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Source d’acquisition</label>
          <Input name="source" value={formData.source} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Canal *</label>
          <CustomSelect
            value={formData.channel || ""}
            onChange={(val) => handleChange({ target: { name: "channel", value: val } })}
            options={[
              { value: "PENDING", label: "En attente" },
              { value: "INBOUND", label: "Entrant (Inbound)" },
              { value: "OUTBOUND", label: "Sortant (Outbound)" },
              { value: "REFERRAL", label: "Recommandation" },
              { value: "EVENT", label: "Événement / Salon" },
              { value: "DIRECT", label: "Direct" },
              { value: "LINKEDIN", label: "LinkedIn" },
              { value: "EMAIL", label: "Emailing" },
              { value: "PHONE", label: "Téléphone" }
            ]}
          />
        </div>
      </>
    ),
    4: isPM ? (
      <>
        <div className="col-span-full relative">
          <label className="text-sm font-semibold text-subtle mb-1 block">Adresse *</label>
          <Input
            name="address"
            value={formData.address}
            onChange={handleAddressChange}
            hasError={!!errors.address}
            className={`${errors.address ? 'pr-10' : ''}`}
            placeholder="Saisissez une adresse..."
          />
          {errors.address && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 pt-6">
              <AlertCircle className="h-5 w-5 text-[var(--hp-danger-600)]" aria-hidden="true" />
            </div>
          )}
          {errors.address && <p className="mt-1.5 text-xs font-medium text-[var(--hp-danger-600)]">{errors.address}</p>}
          {showAddressSuggestions && addressSuggestions?.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-workspace">
              {addressSuggestions.map((feature, idx) => (
                <li
                  key={idx}
                  onClick={() => selectAddress(feature)}
                  className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-ink-900 hover:bg-accent-50 hover:text-accent-900"
                >
                  <span className="block truncate">{feature.properties.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Code Postal</label>
          <Input name="postalCode" value={formData.postalCode} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Ville</label>
          <Input name="city" value={formData.city} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Pays</label>
          <Input name="country" value={formData.country} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Source d’acquisition</label>
          <Input name="source" value={formData.source} onChange={handleChange} />
        </div>
        <div>
          <label className="text-sm font-semibold text-subtle mb-1 block">Canal *</label>
          <CustomSelect
            value={formData.channel || ""}
            onChange={(val) => handleChange({ target: { name: "channel", value: val } })}
            options={[
              { value: "PENDING", label: "En attente" },
              { value: "INBOUND", label: "Entrant (Inbound)" },
              { value: "OUTBOUND", label: "Sortant (Outbound)" },
              { value: "REFERRAL", label: "Recommandation" },
              { value: "EVENT", label: "Événement / Salon" },
              { value: "DIRECT", label: "Direct" },
              { value: "LINKEDIN", label: "LinkedIn" },
              { value: "EMAIL", label: "Emailing" },
              { value: "PHONE", label: "Téléphone" }
            ]}
          />
        </div>
      </>
    ) : (
      <>
        <div className="col-span-full relative">
          <label className="text-sm font-semibold text-subtle">Commentaire</label>
          <textarea name="interestNotes" value={formData.interestNotes} onChange={handleChange} rows={4} maxLength={200} className="flex w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] resize-none mt-1" />
          <span className="absolute bottom-2 right-3 text-[10px] text-muted pointer-events-none">
            {(formData.interestNotes || "").length}/200
          </span>
        </div>
      </>
    ),
    5: (
      <div className="col-span-full relative">
        <label className="text-sm font-semibold text-subtle">Commentaire</label>
        <textarea name="interestNotes" value={formData.interestNotes} onChange={handleChange} rows={4} maxLength={200} className="flex w-full rounded-[var(--hp-radius-md)] border border-[var(--hp-color-border)] bg-[var(--hp-color-surface)] px-3 py-2 text-sm text-[var(--hp-color-text)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hp-color-focus)] resize-none mt-1" />
        <span className="absolute bottom-2 right-3 text-[10px] text-muted pointer-events-none">
          {(formData.interestNotes || "").length}/200
        </span>
      </div>
    ),
  };

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-[60] p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative workspace-card-elevated w-full max-w-3xl p-8 rounded-2xl max-h-[90vh] overflow-y-auto flex flex-col"
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ❌ Fermeture */}
        <button onClick={onClose} className="absolute top-6 right-6 workspace-icon-button">
          <XCircle className="w-5 h-5" />
        </button>

        {/* 🧭 Étape + titre */}
        <h2 className="text-2xl font-bold text-primary flex items-center mb-6">
          {steps[step - 1].icon}
          Étape {step} : {steps[step - 1].label}
        </h2>

        {/* 🔵 Barre de progression */}
        <div className="w-full bg-surface-muted rounded-full h-2 mb-8 overflow-hidden">
          <div className="bg-primary h-2 rounded-full transition-all duration-300" style={{ width: `${(step / steps.length) * 100}%` }} />
        </div>

        {/* 🧾 Champs dynamiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
          {stepFields[step]}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-workspace">
          <button
            disabled={step === 1}
            onClick={() => setStep((s) => s - 1)}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>

          {step < steps.length ? (
            <button
              onClick={() => {
                if (step === 1 && !validateStep1()) return;
                setStep((s) => s + 1);
              }}
              className="btn btn-primary"
            >
              Suivant
            </button>
          ) : (
            <button
              onClick={() => setShowConfirmSubmit(true)}
              disabled={loading}
              className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700 text-white"
            >
              {loading ? "Enregistrement..." : lead ? "Modifier le lead" : "Créer le lead"}
            </button>
          )}
        </div>
      </motion.div>

      {/* ✅ Modale de Confirmation */}
      <AnimatePresence>
        {showConfirmSubmit && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm z-[70] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="workspace-card-elevated w-full max-w-sm p-6 flex flex-col items-center text-center"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className="w-12 h-12 rounded-full bg-success-100 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2">Confirmer l'enregistrement</h3>
              <p className="text-subtle text-sm mb-6">
                Êtes-vous sûr de vouloir {lead ? "modifier" : "ajouter"} ce lead ? Toutes les informations ont-elles été correctement vérifiées ?
              </p>
              <div className="flex w-full gap-3">
                <button
                  className="btn btn-secondary flex-1"
                  onClick={() => setShowConfirmSubmit(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700 text-white flex-1"
                  onClick={() => {
                    setShowConfirmSubmit(false);
                    handleSubmit();
                  }}
                  disabled={loading}
                >
                  {loading ? "En cours..." : "Confirmer"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


    </motion.div>
  );
}
